import React, { Suspense, lazy } from "react";
import ReactDOM from "react-dom/client";
import { App } from "./App";
import { Provider } from "react-redux";
import { store } from "./store";
import { getRootMetadata } from "./utils/getRootMetadata";
import { getGoogleScripts } from "./utils/getGoogleScripts";
import { Variants } from "./configs/configs.types";
import { getGtmDomain, getGtmId, isGtmEnabled } from "./utils/getGtmDomain";
import * as Sentry from "@sentry/react";
import { shouldSendToSentry } from "./utils/environment";
import { ErrorBoundary } from "./components/ErrorBoundary";
import { initChunkLoadRecovery } from "./utils/chunkLoadRecovery";
import { getDomain } from "./utils/getTld";
import { getDtpScriptText } from "./utils/getDtpScriptText";
import { getBrandIconPaths } from "./utils/setSiteMeta";

import "./index.sass";

initChunkLoadRecovery();

const CookieBot = lazy(() => import("react-cookiebot"));
const LoadAnalyticsScripts = lazy(
  () => import("./components/LoadAnalyticScripts/LoadAnalyticScripts").then(module => ({
    default: module.LoadAnalyticsScripts,
  })),
);

// Инициализируем Sentry только в продакшене
if (shouldSendToSentry()) {
  Sentry.init({
    dsn: "https://229831659a51356cd8e1c94c2134e482@o4507063141335040.ingest.us.sentry.io/4509071392505856",
    release: process.env.REACT_APP_SENTRY_RELEASE || "frontend@unknown",
    environment: process.env.REACT_APP_ENVIRONMENT || 'unknown',
    // После деплоя старые вкладки запрашивают несуществующие чанки с прежним хешем — не баг приложения.
    // Стили с хешем в имени могут не догрузиться (сеть, Safari, кэш HTML) — SDK шлёт resource_load_error.
    ignoreErrors: [
      /Failed to fetch dynamically imported module/i,
      /Resource load failed:\s*stylesheet/i,
    ],
    // Включаем отслеживание производительности
    tracesSampleRate: 0.1, // 10% трассировок
    replaysSessionSampleRate: 0.1, // 10% сессий
    replaysOnErrorSampleRate: 1.0, // 100% ошибок
  });
  (window as any).SentryCaptureMessage = Sentry.captureMessage;
} else {
  // В не-продакшене создаем заглушки для совместимости
  (window as any).SentryCaptureMessage = () => {};
  (window as any).Sentry = {
    captureException: () => {},
    captureMessage: () => {}
  };
}

const { title, description } = getRootMetadata();
const ogImageAlt = title; // alt теперь равен title
const canonical = window.location.origin + window.location.pathname;
const region = getDomain();
const ogImage = getBrandIconPaths(region, process.env.REACT_APP_VARIANT || Variants.v0).logo512;
let locale = "en_US";
switch (region) {
  case "cz":
  case "cz_test":
    locale = "cs_CZ";
    break;
  case "kz":
  case "kz_kz":
    locale = "kk_KZ";
    break;
  case "mx":
  case "mx_2":
    locale = "es_MX";
    break;
  case "ro":
    locale = "ro_RO";
    break;
  case "za":
  case "za_2":
    locale = "en_ZA";
    break;
  default:
    locale = "en_US";
}

const root = ReactDOM.createRoot(
  document.getElementById("root") as HTMLElement
);

const variant = process.env.REACT_APP_VARIANT || Variants.v0;
// v4 (Incash) и v5 (Twalalend) переиспользуют вёрстку v0 — глобальные стили завязаны на
// класс-обёртку "v0" (#root > .v0 ...). v5 дополнительно получает класс "v5" для цветовой темы
// (theme-v5.sass). Логотип/фавикон переключаются по REACT_APP_VARIANT напрямую.
const layoutClass = variant === Variants.v4 || variant === Variants.v5 ? Variants.v0 : variant
const themeClass = variant === Variants.v5 ? Variants.v5 : ""
const styleVariant = [layoutClass, themeClass].filter(Boolean).join(" ")

const gaID = getGtmId();
const gtmDomain = getGtmDomain();
const gtmEnabled = isGtmEnabled();
const isGtmPath = gtmDomain.startsWith("/");
const gtmScriptUrl = isGtmPath
  ? gtmDomain
  : `https://${gtmDomain}/gtm.js`;

const { scriptName, gaScriptUrl } =
  process.env.REACT_APP_ENVIRONMENT === "prod"
    ? getGoogleScripts(region)
    : { scriptName: null, gaScriptUrl: null };
const cookiebotDomainGroupId = process.env.REACT_APP_COOKIEBOT_DOMAIN_GROUP_ID;

// Voluum-скрипт колеса (/succes) — отдельная кампания от /offers, тот же трекинг-домен
// региона (track.incash.ro), поэтому scriptName переопределяем только для этого пути.
const WHEEL_VOLUUM_SCRIPT_NAME = "02e97e79-9dbd-44a9-841b-e0e5d62368a3.js";
// Voluum-скрипт страницы бонусных программ (/beneficios) — отдельная кампания от /offers,
// тот же трекинг-домен региона (track.finero.mx).
const BENEFICIOS_VOLUUM_SCRIPT_NAME = "5460d629-d2d0-467b-8442-aa2cd307d0e4.js";
// Voluum-скрипт лендинга со списком офферов (/credit-gratuit) — отдельная кампания от /offers,
// тот же трекинг-домен региона (track.incash.ro).
const OFFERWALL_VOLUUM_SCRIPT_NAME = "f5aa1ac9-ec36-4e0d-9ef1-bddec838b014.js";
// Voluum-скрипт лендинга с сейфом (/seif) — отдельная кампания от /offers,
// тот же трекинг-домен региона (track.incash.ro).
const SAFE_VOLUUM_SCRIPT_NAME = "29bce202-e356-4b5f-9fb1-b0c2006b8d40.js";
// Voluum-скрипт лендинга «plată în rate» (/plata-în-rate) — отдельная кампания от /offers,
// тот же трекинг-домен региона (track.incash.ro).
const RATE_VOLUUM_SCRIPT_NAME = "51ef6dde-7de4-49a8-bcad-20a4c20cce3d.js";
// Voluum-скрипт лендинга с пиццей (/ia-cât-ai-nevoie) — отдельная кампания от /offers,
// тот же трекинг-домен региона (track.incash.ro).
const PIZZA_VOLUUM_SCRIPT_NAME = "84e703af-df8a-4857-95a1-77489885abee.js";
// Voluum-скрипт лендинга «alege drumul scurt» (/drumul-scurt) — отдельная кампания от /offers,
// тот же трекинг-домен региона (track.incash.ro).
const DRUMUL_SCURT_VOLUUM_SCRIPT_NAME = "05be3257-5f70-481b-a305-0f301de27820.js";
const isOffersPath =
  window.location.pathname.startsWith("/offers") ||
  window.location.hostname.includes("offers") ||
  window.location.hostname.includes("loans");
const isSuccessWheelPath = window.location.pathname.startsWith("/succes");
const isBeneficiosPath = window.location.pathname.startsWith("/beneficios");
const isOfferwallLandingPath = window.location.pathname.startsWith("/credit-gratuit");
const isSafeLandingPath = window.location.pathname.startsWith("/seif");
// pathname приходит percent-encoded ("/plata-%C3%AEn-rate"), поэтому сравниваем с закодированным путём.
const isRateLandingPath = window.location.pathname.startsWith(encodeURI("/plata-în-rate"));
const isPizzaLandingPath = window.location.pathname.startsWith(encodeURI("/ia-cât-ai-nevoie"));
const isDrumulScurtLandingPath = window.location.pathname.startsWith("/drumul-scurt");

const activeScriptName = isSuccessWheelPath
  ? WHEEL_VOLUUM_SCRIPT_NAME
  : isBeneficiosPath
  ? BENEFICIOS_VOLUUM_SCRIPT_NAME
  : isOfferwallLandingPath
  ? OFFERWALL_VOLUUM_SCRIPT_NAME
  : isSafeLandingPath
  ? SAFE_VOLUUM_SCRIPT_NAME
  : isRateLandingPath
  ? RATE_VOLUUM_SCRIPT_NAME
  : isPizzaLandingPath
  ? PIZZA_VOLUUM_SCRIPT_NAME
  : isDrumulScurtLandingPath
  ? DRUMUL_SCURT_VOLUUM_SCRIPT_NAME
  : scriptName;

const dtpBaseUrl =
  gaScriptUrl && activeScriptName
    ? `https://${gaScriptUrl}/d/${activeScriptName}`
    : "";

// Вставляем через document.createElement + appendChild (а не JSX/react-helmet-async) —
// это единственный способ, гарантирующий, что браузер реально ВЫПОЛНИТ инлайн-скрипт
// (текст должен быть выставлен до вставки узла в документ).
if (gtmEnabled) {
  const gtmScript = document.createElement("script");
  gtmScript.id = "google-tag-manager";
  gtmScript.text = `(function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
    new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
    j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
    '${gtmScriptUrl}?id='+i+dl;f.parentNode.insertBefore(j,f);
    })(window,document,'script','dataLayer', '${gaID}');`;
  document.head.appendChild(gtmScript);
}

const shouldLoadTransactionIdScript =
  Boolean(gaScriptUrl) &&
  Boolean(activeScriptName) &&
  (isOffersPath ||
    isSuccessWheelPath ||
    isBeneficiosPath ||
    isOfferwallLandingPath ||
    isSafeLandingPath ||
    isRateLandingPath ||
    isPizzaLandingPath ||
    isDrumulScurtLandingPath);

if (shouldLoadTransactionIdScript) {
  const dtpScript = document.createElement("script");
  dtpScript.id = "transaction-id-script";
  dtpScript.text = getDtpScriptText({
    dtpBaseUrl,
    isWheelPath: isSuccessWheelPath || isSafeLandingPath,
    gtmEnabled
  });
  document.head.appendChild(dtpScript);
}

root.render(
  <React.StrictMode>
    <Provider store={store}>
      {/* React 19 хойстит title/meta/link/style в <head> сам по себе, откуда бы в дереве
          они ни рендерились — react-helmet-async больше не нужен для этого. Инлайновые
          трекинг-скрипты (GTM/DTP) вставлены выше через document.createElement, а не тут —
          так браузер гарантированно их выполняет (см. комментарий у shouldLoadTransactionIdScript). */}
      <title>{title}</title>
      <meta charSet="utf-8" />
      <meta name="viewport" content="width=device-width, initial-scale=1, maximum-scale=1" />
      <meta name="theme-color" content="#000000" />
      <meta name="description" content={description} />
      <meta
        httpEquiv="delegate-ch"
        content={`sec-ch-ua https://${gaScriptUrl}; sec-ch-ua-mobile https://${gaScriptUrl}; sec-ch-ua-arch https://${gaScriptUrl}; sec-ch-ua-model https://${gaScriptUrl}; sec-ch-ua-platform https://${gaScriptUrl}; sec-ch-ua-platform-version https://${gaScriptUrl}; sec-ch-ua-bitness https://${gaScriptUrl}; sec-ch-ua-full-version-list https://${gaScriptUrl}; sec-ch-ua-full-version https://${gaScriptUrl}`}
      />
      <meta name="robots" content="index, follow" />
      <meta name="googlebot" content="index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1" />
      <meta httpEquiv="x-ua-compatible" content="IE=edge" />
      <link rel="canonical" href={canonical} />
      <meta property="og:type" content="website" />
      <meta property="og:url" content={canonical} />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      {ogImage && <meta property="og:image" content={ogImage} />}
      {ogImage && <meta property="og:image:alt" content={ogImageAlt} />}
      <meta property="og:site_name" content={title} />
      <meta property="og:locale" content={locale} />
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      {ogImage && <meta name="twitter:image" content={ogImage} />}
      {ogImage && <meta name="twitter:image:alt" content={ogImageAlt} />}

      <script type="application/ld+json">{`
        {
          "@context": "https://schema.org",
          "@type": "Organization",
          "name": "${title}",
          "url": "${window.location.origin}/",
          ${ogImage ? `"logo": "${window.location.origin}${ogImage}",` : ""}
          "sameAs": []
        }
      `}</script>
      <style>{`.dtpcnt{opacity: 0;}`}</style>
      <Suspense fallback={null}>
        {cookiebotDomainGroupId && (
          <ErrorBoundary>
            <CookieBot domainGroupId={cookiebotDomainGroupId} />
          </ErrorBoundary>
        )}
        {gtmEnabled && <LoadAnalyticsScripts gaID={gaID} />}
      </Suspense>
      <div className={`${styleVariant}`}>
        <App />
      </div>
    </Provider>
  </React.StrictMode>
);
